import '../FileUploader/FileUploader.scss';

import { IconLink, IconSearch, IconUpload } from '@hulu-react-style-components/icons';
import React, { useState } from 'react';
import type { FileRejection } from 'react-dropzone';
import { useDropzone } from 'react-dropzone';

import type { AssetType } from '../../configs';
import bem from '../../utils/bem';
import type { Dimensions } from '../AdForm/CreativeBuilders/CreativeBuilder';
import InfoSection from '../FileUploader/InfoSection/InfoSection';
import ProgressContainer from '../FileUploader/ProgressContainer/ProgressContainer';
import { ACCEPTABLE_FILE_TYPES_MAP } from './const';
import { getFilesFromEvent, getFormatDimensionLabelsValue, getMainInfoText, validateFile } from './utils';

export interface AssetUploaderProps {
  assetType: AssetType;
  assetName?: string | null;
  adTypeDimensions?: Dimensions;
  switchToSearch: () => void;
  switchToTag?: () => void;
  onDrop: (acceptedFiles: File[], fileRejections: FileRejection[]) => void;
  aspectRatio: number;
  loading: boolean;
  percentUploaded?: number;
  allowVast: boolean;
  readonly?: boolean;
  cancelRequest: () => void;
}

const [, element] = bem('file-uploader');

function AssetUploader({
  assetType,
  assetName,
  switchToSearch,
  switchToTag,
  onDrop,
  loading,
  percentUploaded,
  cancelRequest,
  adTypeDimensions,
  allowVast,
  readonly,
}: AssetUploaderProps): JSX.Element {
  const [hovering, setHovering] = useState<boolean>(false);
  const onDragEnter = (): void => setHovering(true);
  const onDragLeave = (): void => setHovering(false);

  const { getRootProps, getInputProps } = useDropzone({
    accept: ACCEPTABLE_FILE_TYPES_MAP[assetType],
    multiple: false,
    disabled: readonly,
    onDragEnter,
    onDragLeave,
    onDrop,
    getFilesFromEvent,
    validator: (file: File) => validateFile(file, adTypeDimensions),
  });

  const onClickSearch = (e: React.MouseEvent<HTMLButtonElement, MouseEvent>): void => {
    // Prevent Dropzone from activating
    e.stopPropagation();
    switchToSearch();
  };

  const onClickTagUpload = (e: React.MouseEvent<HTMLButtonElement, MouseEvent>): void => {
    // Prevent Dropzone from activating
    e.stopPropagation();
    switchToTag && switchToTag();
  };

  const messaging = allowVast ? 'Click link to add VAST/VPAID URL:' : 'Drop files here or import from:';
  const formatDimensionLabels = getFormatDimensionLabelsValue(assetType, adTypeDimensions);

  return loading ? (
    <ProgressContainer
      cancelRequest={cancelRequest}
      uploadMessage={
        <>
          Uploading <strong>{assetName}</strong>...
        </>
      }
      percentUploaded={percentUploaded}
    />
  ) : (
    <div data-testid="drop-input" {...getRootProps({ className: element('dropzone', hovering ? 'hovering' : '') })}>
      {!allowVast && <input aria-label="file drag and drop" {...getInputProps()} />}
      <p className={element('head-info')}>{messaging}</p>
      <div className={element('select-container')}>
        {!allowVast && (
          <>
            <button
              data-testid="my-device-upload"
              type="button"
              className={element('select-button')}
              disabled={readonly}
            >
              <IconUpload title="upload icon" />
              <span>My Device</span>
            </button>
            <button
              data-testid="file-library-upload"
              type="button"
              className={element('select-button')}
              onClick={onClickSearch}
              disabled={readonly}
            >
              <IconSearch title="search icon" />
              <span>File Library</span>
            </button>
          </>
        )}
        {allowVast
          ? switchToTag && (
              <button
                type="button"
                className={element('select-button')}
                onClick={onClickTagUpload}
                disabled={readonly}
                data-testid="vast-url-tag"
              >
                <IconLink title="link icon" />
                <span>VAST/VPAID</span>
              </button>
            )
          : null}
      </div>
      <InfoSection
        mainInfo={getMainInfoText(assetType, allowVast)}
        formatDimensionLabels={(!allowVast && formatDimensionLabels) || undefined}
      />
    </div>
  );
}

export default AssetUploader;
